import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';

/**
 * 管理者ユーザーのドメイン型。
 * admin-console-api の adminUsers テーブル準拠。
 */
export interface AdminUser {
  id: string;
  uid: string;
  email: string;
  displayName: string;
  status: 'active' | 'disabled';
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
}

/**
 * GET /api/v1/admin-users のレスポンス型。
 */
export interface AdminUserListResponse {
  items: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * GET /api/v1/admin-users/:id のレスポンス型。
 */
export interface AdminUserDetailResponse {
  user: AdminUser;
}

/**
 * POST /api/v1/admin-users のリクエスト型。
 */
export interface CreateAdminUserInput {
  email: string;
  displayName: string;
}

/**
 * POST /api/v1/admin-users のレスポンス型（201）。
 */
export interface CreateAdminUserResponse {
  user: AdminUser;
}

/**
 * PATCH /api/v1/admin-users/:id/disable のレスポンス型。
 */
export interface DisableAdminUserResponse {
  user: {
    id: string;
    uid: string;
    email: string;
    displayName: string;
    status: 'active' | 'disabled';
  };
}

/**
 * 管理者ユーザー一覧クエリのパラメータ型。
 * API は page / pageSize を受け取る（MasterListParams とは異なる）。
 */
export interface AdminUserListParams {
  page: number;
  pageSize: number;
}

const BASE = '/admin-users';

/**
 * 管理者ユーザー用 queryOptions factory。
 * `useQuery` / `ensureQueryData` / `prefetchQuery` で使い回す。
 */
export const adminUserQueries = {
  all: ['admin-users'] as const,
  list: (params: AdminUserListParams) =>
    queryOptions({
      queryKey: ['admin-users', 'list', params] as const,
      queryFn: async () => {
        const { data } = await apiClient.get<AdminUserListResponse>(BASE, {
          page: params.page,
          pageSize: params.pageSize,
        });
        return data;
      },
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: ['admin-users', 'detail', id] as const,
      queryFn: async () => {
        const { data } = await apiClient.get<AdminUserDetailResponse>(`${BASE}/${id}`);
        return data;
      },
    }),
};

/**
 * 管理者ユーザー作成。
 * 409 や 400 エラーは ApiError としてスローされる。
 */
export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAdminUserInput) => {
      const { data } = await apiClient.post<CreateAdminUserResponse>(BASE, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminUserQueries.all });
    },
  });
}

/**
 * 管理者ユーザー無効化（PATCH /admin-users/:id/disable）。
 * 成功後は一覧・詳細両方を再取得する。
 */
export function useDisableAdminUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.patch<DisableAdminUserResponse>(`${BASE}/${id}/disable`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminUserQueries.all });
      qc.invalidateQueries({ queryKey: ['admin-users', 'detail', id] });
    },
  });
}
